import { CreativeOSFeatures, CreativeOSPages, PlanType, subscriptionPlans } from '@/_shared/subscriptionPlans'
import { FREE_PLAN, PRO_PLAN, SPARK_PLAN, STANDARD_PLAN } from '@/utils/data/plans'
import { trpc } from '@/utils/trpc'
import React from 'react'

const useUserPermissions = () => {
  const {
    data: permissions,
    isLoading,
    error,
  } = trpc.permissions.useQuery(null, {
    staleTime: 1000 * 60 * 2, // Cache data for 2 minutes
  })

  const { data: authUser, isLoading: isFetchingAuthUser } = trpc.me.useQuery(null, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // Cache data for 2 minutes
  })
  const { data: userTeams, isLoading: isFetchingUserTeams } = trpc.getUserTeams.useQuery(
    {},
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2, // Cache data for 2 minutes
    },
  )
  const {
    data: userLimits,
    isLoading: isGettingUserLimits,
    isRefetching: isRefetchingUserLimits,
  } = trpc.getUserLimits.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // Cache data for 2 minutes
  })

  const currentPlanConfigs =
    ((permissions?.usersPlans ?? []) as PlanType[])
      ?.map((planId: PlanType) => subscriptionPlans[planId])
      .filter((plan) => plan !== undefined && plan !== null) ?? []

  const userHasAccessToPage = React.useMemo(
    () =>
      (page?: CreativeOSPages): boolean => {
        if (!page) return true
        return currentPlanConfigs.some(
          (config) => config.accessiblePages && Object.keys(config.accessiblePages).includes(page),
        )
      },
    [currentPlanConfigs],
  )

  const userHasAccessToFeature = React.useMemo(() => {
    return (feature?: CreativeOSFeatures): boolean => {
      if (!feature) return true

      const hasFeatureInPlan = currentPlanConfigs.some((config) => config.features.includes(feature))

      if (!hasFeatureInPlan) return false

      if (!userLimits) return false

      switch (feature) {
        case CreativeOSFeatures.AD_INSPIRATION_REQUEST:
          return userLimits.request.ad > 0
        case CreativeOSFeatures.EMAIL_INSPIRATION_REQUEST:
          return userLimits.request.email > 0
        case CreativeOSFeatures.LANDER_INSPIRATION_REQUEST:
          return userLimits.request.lander > 0
        case CreativeOSFeatures.USE_FCA:
          return userLimits.funnelCongruenceAnalyzerLimit > 0
        case CreativeOSFeatures.USE_CA:
          return userLimits.creativeAnalystLimit > 0
        case CreativeOSFeatures.USE_CREATIVE_TOOLKIT:
          return userLimits.creativeToolkit > 0
        case CreativeOSFeatures.ADD_NEW_COLLECTION:
          return userLimits.userCollections > 0
        default:
          return true
      }
    }
  }, [currentPlanConfigs, userLimits])

  return {
    userLimits,
    isGettingUserLimits,
    authUser,
    isFetchingAuthUser,
    isRefetchingUserLimits,
    isProUser: permissions
      ? permissions.usersPlans.includes(PRO_PLAN.no_trial_plan_id) || permissions.usersPlans.includes(PRO_PLAN.plan_id)
      : false,
    userIsOnFreePlan:
      permissions?.usersPlans &&
      !isLoading &&
      (permissions?.usersPlans.includes(FREE_PLAN.plan_id) ||
        permissions?.usersPlans.includes(FREE_PLAN.no_trial_plan_id) ||
        permissions?.usersPlans.includes(FREE_PLAN.yearly_plan_id)),

    userIsOnIgnitePlan:
      permissions?.usersPlans &&
      !isLoading &&
      (permissions?.usersPlans.includes(STANDARD_PLAN.plan_id) ||
        permissions?.usersPlans.includes(STANDARD_PLAN.no_trial_plan_id) ||
        permissions?.usersPlans.includes(STANDARD_PLAN.yearly_plan_id)),

    userIsOnSparkPlan:
      permissions?.usersPlans &&
      !isLoading &&
      (permissions?.usersPlans.includes(SPARK_PLAN.plan_id) ||
        permissions?.usersPlans.includes(SPARK_PLAN.no_trial_plan_id) ||
        permissions?.usersPlans.includes(SPARK_PLAN.yearly_plan_id)),
    permissions,
    isLoading,
    error,
    currentPlanConfigs,
    userTeams,
    isFetchingUserTeams,
    userHasAccessToPage,
    userHasAccessToFeature,
  }
}

export default useUserPermissions
